import { inject, Injectable } from '@angular/core';
import { AlertService, SpinService } from '../services';
import { AuthService } from './auth.service';
import { isMfaRequiredError, MfaChannel } from './mfa';

/**
 * Collects a short-lived MFA token in the UI and retries step-up mutations.
 */
@Injectable({ providedIn: 'root' })
export class MfaStepUpService {
  private readonly authService = inject(AuthService);
  private readonly alertService = inject(AlertService);
  private readonly spinService = inject(SpinService);

  /**
   * Requests the challenge, asks for the 6-digit code, and returns the mfaToken.
   * Returns null when the user cancels the prompt.
   */
  public async obtainToken(channel: MfaChannel): Promise<string | null> {
    this.spinService.start();
    try {
      await this.authService.requestMfa(channel);
    } finally {
      this.spinService.stop();
    }
    const destination: string = channel === 'whatsapp' ? 'WhatsApp' : 'e-mail';
    const result = await this.alertService.prompt({
      title: 'Código de verificação',
      message: `Digite o código de 6 dígitos enviado ao ${destination}.`,
      placeholder: '000000',
      confirmButtonText: 'Confirmar',
    });
    if (!result.isConfirmed) {
      return null;
    }
    const code: string = String(result.value ?? '').replace(/\D/g, '');
    if (code.length !== 6) {
      throw new Error('Informe o código de 6 dígitos.');
    }
    this.spinService.start();
    try {
      const mfaToken: string = await this.authService.verifyMfa(channel, code);
      return mfaToken;
    } finally {
      this.spinService.stop();
    }
  }

  /**
   * Runs an auth mutation and, on MFA_REQUIRED, collects a token and retries once.
   * Returns false when the user cancels the step-up prompt.
   */
  public async runWithStepUp(action: (mfaToken?: string) => Promise<void>, resolveChannel: () => MfaChannel): Promise<boolean> {
    try {
      await action();
      return true;
    } catch (error: unknown) {
      if (!isMfaRequiredError(error)) {
        throw error;
      }
      const mfaToken: string | null = await this.obtainToken(resolveChannel());
      if (!mfaToken) {
        return false;
      }
      await action(mfaToken);
      return true;
    }
  }
}
